/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { TOptions } from 'i18next';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';

import { Namespaces } from '../../../i18n';
import { useCreateEntityOverlayFormStyles } from '../../../styles/createEntityOverlay';
import { useFormStyles } from '../../../styles/form';
import { useMaterialPickersStyles } from '../../../styles/materialPickers';
import { getDateFormatByLocale } from '../../../utils/getDateFormatByLocale';
import { ApiValidationErrors, ApiValidationResetFieldError } from '../../../utils/useApiValidation';
import { useFormatDate } from '../../../utils/useFormatDate';
import { NextStateType } from '../../../utils/useObjectState';
import { useUtcFormat } from '../../../utils/useUtcFormat';
import { CreateClusterFromBackupValues, FetchParamsState } from '../CreateClusterOverlay.types';
import { SelectCluster } from './SelectCluster/SelectCluster';

type CreateClusterStepInfoProps = FormikProps<CreateClusterFromBackupValues> & {
  apiErrors: ApiValidationErrors;
  resetFieldApiError: ApiValidationResetFieldError;
  setFetchParams: NextStateType<FetchParamsState>;
};

export function CreateClusterStepBackup(props: CreateClusterStepInfoProps) {
  const { apiErrors, setFetchParams, ...formProps } = props;
  const { values, setFieldValue, errors, validateForm } = formProps;
  const classes = useCreateEntityOverlayFormStyles();
  const formClasses = useFormStyles();
  const mpClasses = useMaterialPickersStyles();
  const { t, i18n } = useTranslation([Namespaces.CreateCluster, Namespaces.Cluster]);
  const [lastBackupTime] = useState(() => DateTime.utc());
  const { utcLocal, utcServer } = useUtcFormat();
  const { formatDate } = useFormatDate();

  const oldestRestorePoint = values.oldestRestorePoint;
  const firstBackupTime = useMemo(
    () => DateTime.fromISO(oldestRestorePoint || '1900-01-01').toUTC(),
    [oldestRestorePoint],
  );

  useMemo(
    () =>
      setFetchParams({
        organizationId: values.backupOrganizationId,
        projectId: values.backupProjectId,
        clusterId: values.backupClusterId,
      }),
    [setFetchParams, values.backupOrganizationId, values.backupProjectId, values.backupClusterId],
  );

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    void setFieldValue(event.target.name, checked);
  };

  const hasError = (field: keyof CreateClusterFromBackupValues): boolean => {
    return !!errors[field] || !!apiErrors[field];
  };

  const getError = (field: keyof CreateClusterFromBackupValues, options?: TOptions): string => {
    const error = errors[field] || apiErrors[field];
    if (error) {
      return t(`validation.${field}.${error}`, options);
    }
    return '';
  };

  useEffect(() => {
    void setFieldValue('backupTimestampMinDate', firstBackupTime.toJSDate(), false);
    void setFieldValue('backupTimestampMaxDate', lastBackupTime.toJSDate(), false);
    void validateForm();
  }, [firstBackupTime, lastBackupTime, setFieldValue, validateForm]);

  const backupDatetimeErrorOptions = useMemo(
    () => ({
      maxDate: lastBackupTime.toFormat(getDateFormatByLocale(i18n.language)),
      minDate: firstBackupTime.toFormat(getDateFormatByLocale(i18n.language)),
      utc: utcServer,
    }),
    [firstBackupTime, i18n.language, lastBackupTime, utcServer],
  );

  const dateFormat = useMemo(() => getDateFormatByLocale(i18n.language), [i18n.language]);

  const handleBackupDatetimeChange = (value: DateTime | null) => {
    if (!value) {
      void setFieldValue('backupTimestamp', null);
      return;
    }

    if (value.toFormat(dateFormat) === firstBackupTime.toFormat(dateFormat)) {
      void setFieldValue('backupTimestamp', firstBackupTime.toJSDate());
    } else if (value.toFormat(dateFormat) === lastBackupTime.toFormat(dateFormat)) {
      void setFieldValue('backupTimestamp', lastBackupTime.toJSDate());
    } else {
      void setFieldValue('backupTimestamp', value.toJSDate());
    }
  };

  const backupTimestamp = useMemo(
    () => values.backupTimestamp && DateTime.fromJSDate(values.backupTimestamp),
    [values.backupTimestamp],
  );

  const backupTimestampLocal = useMemo(() => {
    const localTime = backupTimestamp && backupTimestamp.toLocal();
    return localTime && localTime.isValid ? `${formatDate(localTime.toISO())} (${utcLocal})` : null;
  }, [backupTimestamp, utcLocal, formatDate]);

  return (
    <>
      <FormControl className={cx(formClasses.formControl, formClasses.widthMedium)} error={hasError('backupCluster')}>
        <SelectCluster {...formProps} purpose="cluster_creation_from_backup" />
        {hasError('backupCluster') && <FormHelperText>{getError('backupCluster')}</FormHelperText>}
      </FormControl>
      <div className={classes.formControlWithLabel}>
        <div className={cx(classes.formControl, classes.widthDefault, classes.datePickerField)}>
          <DateTimePicker
            // renderInput={props => (
            //   <Tooltip placement="top" title={datePickerTooltip}>
            //     <TextField
            //       {...props}
            //       autoFocus={!!values.backupCluster || !!oldestRestorePoint}
            //       label={undefined}
            //       variant="outlined"
            //       error={!!errors.backupTimestamp}
            //       placeholder={t('labels.backup_datetime')}
            //       name="backupTimestamp"
            //       InputProps={{
            //         ...(props.InputProps || {}),
            //         endAdornment: (
            //           <div className={classes.calendarAdornmentRoot}>
            //             {utcServer && <div>({utcServer})</div>}
            //             {props.InputProps?.endAdornment}
            //           </div>
            //         ),
            //       }}
            //     />
            //   </Tooltip>
            // )}
            ampm={false}
            label={t('labels.backup_datetime')}
            value={backupTimestamp}
            format={getDateFormatByLocale(i18n.language)}
            onChange={value => handleBackupDatetimeChange(value)}
            minDateTime={firstBackupTime}
            maxDateTime={lastBackupTime}
            slotProps={{ dialog: { className: mpClasses.popoverRoot } }}
            disabled={!values.backupCluster || !oldestRestorePoint}
          />
          {hasError('backupTimestamp') && (
            <FormHelperText error>{getError('backupTimestamp', backupDatetimeErrorOptions)}</FormHelperText>
          )}
        </div>
        <Typography className={classes.rightFormLabel}>
          {t('labels.local_time')}:
          <br />
          {backupTimestampLocal}
        </Typography>
      </div>

      <FormControl className={cx(formClasses.formControl, formClasses.widthFull)}>
        <Typography variant="h4" className={classes.formTitle}>
          {t('labels.clone_settings')}
        </Typography>
        <FormGroup>
          <FormControlLabel
            className={classes.formControlLabel}
            control={<Checkbox checked={values.cloneAcl} onChange={handleCheckboxChange} name="cloneAcl" />}
            label={t('parameters.clone_acl')}
          />
          <FormControlLabel
            className={classes.formControlLabel}
            control={
              <Checkbox checked={values.cloneParameters} onChange={handleCheckboxChange} name="cloneParameters" />
            }
            label={t('parameters.clone_parameters')}
          />
          <FormControlLabel
            className={classes.formControlLabel}
            control={
              <Checkbox
                checked={values.cloneLogicalBackupConfiguration}
                onChange={handleCheckboxChange}
                name="cloneLogicalBackupConfiguration"
              />
            }
            label={t('parameters.clone_logical_backup_configuration')}
          />
          <FormControlLabel
            className={classes.formControlLabel}
            control={
              <Checkbox
                checked={values.cloneContinuousBackupConfiguration}
                onChange={handleCheckboxChange}
                name="cloneContinuousBackupConfiguration"
              />
            }
            label={t('parameters.clone_continuous_backup_configuration')}
          />
        </FormGroup>
      </FormControl>
    </>
  );
}
